import {
  formatOdds,
  kickoffTime,
  marketLocale,
  resolveApiBase,
  SportsApiClient,
  type OddsFormat,
  type WidgetProps,
} from '@sports/core'
import { useFeed } from '../../use-feed'
import { useLabels } from '../../use-labels'
import { useTimeZone } from '../../use-time-zone'

export const MMA_CARDS_ATTRIBUTES = [
  'promotion',
  'events',
  'lang',
  'heading',
  'refresh',
  'api-base',
  'mma-base',
  'theme',
] as const

interface Props extends WidgetProps {
  promotion?: string
  events?: string
  lang?: string
  heading?: string
  refresh?: string
  apiBase?: string
  mmaBase?: string
}

declare global {
  interface Window {
    __MMA_API_BASE__?: string
  }
}
/** The Cage Brothers MMA service (betbrothers-mma/mma-api), which proxies the
 *  BALLDONTLIE MMA feed: events, fight cards and per-bookmaker fight odds. */
const DEFAULT_MMA_BASE = 'https://mma-api.dev.betbrothers.football'

export const mmaCardsStyles = `
  .mma-row .fx .nm { font-weight: 500; }
  .mma-row .wc {
    grid-column: 2 / 3; margin-top: 0.1rem; text-align: center;
    color: var(--sports-muted); font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.03em;
  }
  .mma-row .wc .me { color: var(--sports-accent); font-weight: 700; }
  .segment {
    padding: 0.3rem 1rem; font-size: 0.68rem; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.04em; color: var(--sports-muted); border-top: 1px solid var(--sports-border);
  }
  .league .when { font-weight: 500; text-transform: none; letter-spacing: 0; }
`

interface Fighter {
  id: number
  name: string
}
interface MmaEvent {
  id: number
  name: string
  date: string
  status: string
  main_card_start_time?: string | null
  prelims_start_time?: string | null
  early_prelims_start_time?: string | null
}
interface MmaFight {
  id: number
  event?: MmaEvent
  fighter1: Fighter
  fighter2: Fighter
  weight_class?: { name?: string } | null
  is_main_event?: boolean
  is_title_fight?: boolean
  card_segment?: string | null
  fight_order?: number | null
  status?: string
}
interface MmaOdds {
  fight_id: number
  vendor: string
  fighter1: Fighter
  fighter2: Fighter
  fighter1_odds: number | null
  fighter2_odds: number | null
}
interface Card {
  event: MmaEvent
  fights: MmaFight[]
  odds: MmaOdds[]
}
interface BookmakerResponse {
  oddsFormat?: OddsFormat
  brand: { name?: string; logo?: string | null; link?: string | null } | null
}

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

/** American moneyline → decimal (+150 → 2.5, -185 → 1.54). */
export function americanToDecimal(american: number | null | undefined): number | null {
  if (american == null || !Number.isFinite(american) || american === 0) return null
  return american > 0 ? 1 + american / 100 : 1 + 100 / Math.abs(american)
}

/** Best (highest) decimal price per fighter id across every vendor's line. */
export function bestPrices(odds: MmaOdds[]): Map<number, number> {
  const best = new Map<number, number>()
  const offer = (fighter: Fighter | undefined, american: number | null) => {
    const price = americanToDecimal(american)
    if (!fighter || price == null) return
    if (price > (best.get(fighter.id) ?? 0)) best.set(fighter.id, price)
  }
  for (const o of odds) {
    offer(o.fighter1, o.fighter1_odds)
    offer(o.fighter2, o.fighter2_odds)
  }
  return best
}

const SEGMENTS = ['main', 'prelims', 'early_prelims'] as const
function segmentOf(fight: MmaFight): string {
  const s = (fight.card_segment ?? '').toLowerCase()
  return s.includes('early') ? 'early_prelims' : s.includes('prelim') ? 'prelims' : 'main'
}

async function getJson<T>(url: string, signal: AbortSignal): Promise<T> {
  const res = await fetch(url, { signal })
  if (!res.ok) throw new Error(`${res.status} ${url}`)
  return (await res.json()) as T
}

/** The next `count` scheduled events (optionally one promotion), each with its
 *  card and odds. A card whose fights or odds fail still renders what it has. */
async function loadCards(mmaBase: string, promotion: string, count: number, signal: AbortSignal): Promise<Card[]> {
  const { data } = await getJson<{ data: MmaEvent[] }>(`${mmaBase}/bdl/events?per_page=60`, signal)
  const needle = promotion.trim().toLowerCase()
  const upcoming = data
    .filter((e) => e.status === 'scheduled' && (!needle || e.name.toLowerCase().includes(needle)))
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, count)
  return Promise.all(
    upcoming.map(async (event) => {
      const [fights, odds] = await Promise.all([
        getJson<{ data: MmaFight[] }>(`${mmaBase}/bdl/fights?event_id=${event.id}`, signal).then((r) => r.data).catch(() => []),
        getJson<{ data: MmaOdds[] }>(`${mmaBase}/bdl/odds?event_id=${event.id}`, signal).then((r) => r.data).catch(() => []),
      ])
      return { event, fights, odds }
    }),
  )
}

/**
 * MMA fight cards — the next events (UFC, PFL, Contender Series… or one
 * promotion via `promotion="ufc"`), each bout with its weight class and a
 * two-way price per fighter. Data from mma-api; the brand, link and odds
 * format are the locale's bookmaker, exactly like the match centres.
 */
export function MmaCards({ promotion = '', events, lang = 'es', heading, refresh, apiBase, mmaBase }: Props) {
  const base = resolveApiBase(apiBase)
  const mma = (mmaBase?.trim() || window.__MMA_API_BASE__?.trim() || DEFAULT_MMA_BASE).replace(/\/+$/, '')
  const count = number(events, 3)
  const t = useLabels(base, lang)
  const tz = useTimeZone()

  const { data, error, loading } = useFeed(
    (signal) => loadCards(mma, promotion, count, signal),
    [mma, promotion, count],
    number(refresh, 600),
  )
  const { data: display } = useFeed(
    (signal) => new SportsApiClient(base).bookmaker<BookmakerResponse>(lang, 'match-center', signal),
    [base, lang],
    0,
  )
  const format: OddsFormat = display?.oddsFormat ?? 'decimal'
  const brand = display?.brand ?? null
  const link = brand?.link ?? undefined

  const dateLocale = marketLocale(lang)
  const dayLabel = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(dateLocale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: tz })
    } catch {
      return iso.slice(0, 10)
    }
  }
  const segmentLabel: Record<string, string> = {
    main: t('mainCard', 'Cartelera principal'),
    prelims: t('prelims', 'Preliminares'),
    early_prelims: t('earlyPrelims', 'Preliminares tempranas'),
  }
  const segmentStart = (event: MmaEvent, segment: string) =>
    segment === 'main' ? event.main_card_start_time : segment === 'prelims' ? event.prelims_start_time : event.early_prelims_start_time

  const cell = (label: string, price: number | undefined, best: boolean) => {
    const text = price != null ? formatOdds(price, format) : ''
    const cls = `cell${best && text ? ' best' : ''}${text ? '' : ' empty'}`
    const inner = (
      <>
        <span class="k">{label}</span>
        {text || '—'}
      </>
    )
    return link && text ? (
      <a class={cls} href={link} target="_blank" rel="sponsored noopener">
        {inner}
      </a>
    ) : (
      <span class={cls}>{inner}</span>
    )
  }
  const bookmaker = () => {
    if (!brand?.logo && !brand?.name) return <span class="bk" />
    const inner = brand?.logo ? <img src={brand.logo} alt={brand.name ?? ''} /> : brand?.name
    return link ? (
      <a class="bk" href={link} target="_blank" rel="sponsored noopener">
        {inner}
      </a>
    ) : (
      <span class="bk">{inner}</span>
    )
  }

  const cards = (data ?? []).filter((c) => c.fights.length)

  return (
    <div class="sports-card">
      {heading && (
        <div class="sports-head">
          <span>{heading}</span>
        </div>
      )}
      {loading && !data && <div class="sports-note">{t('loading', 'Cargando…')}</div>}
      {error && !data && <div class="sports-note">{t('matchesUnavailable', 'Los combates no están disponibles ahora.')}</div>}
      {data && cards.length === 0 && <div class="sports-note">{t('noFights', 'Sin combates programados.')}</div>}
      {cards.map(({ event, fights, odds }) => {
        const prices = bestPrices(odds)
        return (
          <div class="mma-event" key={event.id}>
            <div class="league">
              <span class="lg">{event.name}</span>
              <span class="when">{dayLabel(event.date)}</span>
            </div>
            {SEGMENTS.map((segment) => {
              const bouts = fights
                .filter((f) => segmentOf(f) === segment)
                // Top of the card first: the feed numbers bouts from the main
                // event down (fight_order 1 = main event).
                .sort((a, b) => Number(!!b.is_main_event) - Number(!!a.is_main_event) || (a.fight_order ?? 99) - (b.fight_order ?? 99))
              if (!bouts.length) return null
              const start = segmentStart(event, segment) ?? event.date
              const time = kickoffTime(new Date(start), lang, tz)
              return (
                <div key={segment}>
                  <div class="segment">{segmentLabel[segment]}</div>
                  {bouts.map((f) => {
                    const p1 = prices.get(f.fighter1.id)
                    const p2 = prices.get(f.fighter2.id)
                    const fav = p1 != null && p2 != null ? (p1 <= p2 ? 1 : 2) : 0
                    return (
                      <div class="row mma-row" key={f.id}>
                        <span class="time">{time}</span>
                        <div class="fx">
                          <span class="side">
                            <span class="nm">{f.fighter1.name}</span>
                          </span>
                          <span class="sc pending">vs</span>
                          <span class="side away">
                            <span class="nm">{f.fighter2.name}</span>
                          </span>
                        </div>
                        <div class="odds two">
                          {cell('1', p1, fav === 1)}
                          {cell('2', p2, fav === 2)}
                        </div>
                        {bookmaker()}
                        <div class="wc">
                          {f.is_main_event && <span class="me">{t('mainEvent', 'Combate estelar')} · </span>}
                          {f.weight_class?.name ?? ''}
                          {f.is_title_fight ? ` · ${t('titleFight', 'Título')}` : ''}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
